<script lang="ts" setup>
import { ref } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { ArchiveBoxIcon, BellAlertIcon, CurrencyEuroIcon, BuildingOfficeIcon, ClockIcon, CreditCardIcon, ChatBubbleLeftRightIcon, FolderOpenIcon, ListBulletIcon, PaperAirplaneIcon, PencilSquareIcon, QueueListIcon, RectangleStackIcon, UserIcon, UsersIcon, DocumentTextIcon, BuildingLibraryIcon, ForwardIcon } from '@heroicons/vue/20/solid'
import FilterMenu from './FilterMenu.vue';
import type { AdminRole } from '@upclaim/sdk/src/extra-types';
import AdditionalLossFilter from "./ais/tabs-filters/AdditionalLossFilters.vue";
import SignatureFilters from "./ais/tabs-filters/SignatureFilters.vue";
import ProcedureFilters from "./ais/tabs-filters/ProcedureFilters.vue";
import WaitForConfirmationFilters from "./ais/tabs-filters/WaitForConfirmationFilters.vue";
import ArchivedFilters from "./ais/tabs-filters/ArchivedFilters.vue";
import { ScaleIcon } from '@heroicons/vue/24/solid';

const emits = defineEmits<{
  (e: 'tabUpdated', filter: string): void
}>();

interface Filter {
  name: string;
  filter: string;
  permissions: Array<AdminRole> | "*";
  advancedFilter?: any;
  icon: any;
  current: boolean;
}

const currentAdminEmail = useAuth0().user.value?.email;
// `isLawyer` is a quick fix - In the near future we'll use API generated scoped keys
const isLawyer = Boolean(useAuth0().user.value?.["https://netlify-integration.com/roles"]?.includes("LAWYER"));
const tabs: Filter[] = [
  isLawyer 
    ? { name: 'Dossiers du cabinet', filter: "claimStates:COURT.WAIT_FOR_JURIDCTION_CHOICE AND (claimStateRemainingDelay < 0 OR claimStateRemainingDelay >= 0)", permissions: ['LAWYER'], icon: QueueListIcon, advancedFilter: ProcedureFilters, current: true }
    : { name: 'Tout voir', filter: "NOT claimLegalStepName:CANCELLED AND NOT claimLegalStepName:LOST", permissions: ['BUSINESS_TRAVEL_ADMIN', 'CUSTOMER_ADMIN', 'CUSTOMER_AGENT', "CUSTOMER_ACCOUNT_MANAGER", 'UPCLAIM_ADMIN'], icon: QueueListIcon, current: true }
  ,
  { name: 'Mes dossiers', filter: `agent.email:${currentAdminEmail} OR assignedAdminEmail:${currentAdminEmail} OR accountManager.email:${currentAdminEmail}`, permissions: ['CUSTOMER_ACCOUNT_MANAGER', 'CUSTOMER_AGENT'], icon: FolderOpenIcon, current: true },
  { name: 'Attente Confirmation', filter: `claimStateType:CLAIM_VALIDATION.WAIT_FOR_AGENT_FEEDBACK`, advancedFilter: WaitForConfirmationFilters, permissions: ['CUSTOMER_ACCOUNT_MANAGER', 'CUSTOMER_ADMIN', 'BUSINESS_TRAVEL_ADMIN', 'UPCLAIM_ADMIN'], icon: BellAlertIcon, current: true },
  { name: 'Attente préjudices', filter: `claimStateType:CLAIM_VALIDATION.WAIT_FOR_ADDITIONAL_LOSS_FEEDBACK`, advancedFilter: AdditionalLossFilter, permissions: ['CUSTOMER_ACCOUNT_MANAGER', 'CUSTOMER_ADMIN', 'BUSINESS_TRAVEL_ADMIN', 'UPCLAIM_ADMIN'], icon: CurrencyEuroIcon, current: true },
  // { name: 'Attente signature', filter: "claimStateType:CLAIM_VALIDATION.WAIT_FOR_RECEIVABLE_CONTRACT_SIGNATURES", advancedFilter: SignatureFilters, permissions: ['UPCLAIM_ADMIN', "CUSTOMER_ADMIN", "CUSTOMER_ACCOUNT_MANAGER", "BUSINESS_TRAVEL_ADMIN"], icon: PencilSquareIcon, current: false },
  { name: 'Contentieux', filter: `claimStates:COURT.WAIT_FOR_JURIDCTION_CHOICE AND NOT claimLegalStepName:CANCELLED AND NOT claimLegalStepName:LOST AND NOT claimLegalStepName:MONEY_RECEIVED`, advancedFilter: ProcedureFilters, permissions: ['CUSTOMER_ACCOUNT_MANAGER', 'CUSTOMER_ADMIN', 'BUSINESS_TRAVEL_ADMIN', 'UPCLAIM_ADMIN'], icon: ScaleIcon, current: true },
  { name: 'À envoyer', filter: "claimStateType:UPCLAIM.WAIT_FOR_1ST_EMAIL_SEND \
    OR claimStateType:UPCLAIM.WAIT_FOR_UPCLAIM_REPLY \
    OR claimStateType:UPCLAIM.WAIT_SENDS_MED \
    OR claimStateType:WAITING_FOR_MONEY.CONCILIATION_WAIT_FOR_TRANSACTIONAL_PROTOCOL_GENERATION \
    OR claimStateType:WAITING_FOR_MONEY.UPCLAIM_WAIT_FOR_FOLLOW_UP_REDACTION \
    OR claimStateType:WAITING_FOR_MONEY.UPCLAIM_WAIT_FOR_PAYMENT_FOLLOW_UP_REPLY \
    OR claimStateType:COURT.WAIT_FOR_ESCP_FORM_A \
    OR claimStateType:WAITING_FOR_MONEY.CONCILIATION_WAIT_FOR_FOLLOW_UP_WITHOUT_PROTOCOLE \
    OR claimStateType:COURT.WAIT_FOR_ESCP_FORM_A_SEND",
    permissions: ["UPCLAIM_ADMIN"], icon: ForwardIcon, current: false },
  { name: 'Attente confirmation', filter: "claimStateType:COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_TRIAL_DATE_CHOICE", permissions: ['LAWYER'], icon: BuildingLibraryIcon, current: false }, 
  { name: 'Assignations', filter: "claimStateType:COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_ASSIGNATION_GENERATION \
    OR claimStateType:COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_TRIAL_DATE_CHOICE", permissions: ['LAWYER'], icon: DocumentTextIcon, current: false }, 
  { name: 'Délai expiré', filter: "claimStateRemainingDelay < 0", permissions: ["LAWYER"], icon: ClockIcon, current: false },
  { name: 'Archivés', filter: "claimLegalStepName:CANCELLED OR claimLegalStepName:LOST OR claimLegalStepName:MONEY_RECEIVED", advancedFilter: ArchivedFilters, permissions: "*", icon: ArchiveBoxIcon, current: false },
]
const currentIndex = ref(0);

function refine(tab: typeof tabs[number], index: number) {
  currentIndex.value = index;
  emits("tabUpdated", tab.filter)
}
</script>
<template>
  <div>
    <div class="sm:hidden">
      <label for="tabs" class="sr-only">Select a tab</label>
      <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
      <select id="tabs" name="tabs" class="block w-full border-gray-300 rounded-md focus:border-blue-500 focus:ring-blue-500">
        <option v-for="tab in tabs" :key="tab.name" :selected="tab.current">{{ tab.name }}</option>
      </select>
    </div>
    <div class="hidden sm:block">
      <div class="border-b border-gray-200">
        <nav class="flex -mb-px space-x-8" aria-label="Tabs">
          <template v-for="(tab, index) in tabs" :key="tab.name">
            <template @click.prevent="refine(tab, index)" v-can="tab.permissions" :class="[index === currentIndex ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700', 'group cursor-pointer inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium']">
              <div @click="refine(tab, index)" class="flex" :aria-current="index === currentIndex ? 'page' : undefined">
                <component :is="tab.icon" :class="[index === currentIndex ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500', '-ml-0.5 mr-2 h-5 w-5']" aria-hidden="true" />
                <span>{{ tab.name }}</span>
                <!-- <FilterMenu class="ml-2" v-if="index === currentIndex" /> -->
              </div>
            <span v-if="tab.advancedFilter"><component v-if="index === currentIndex" :is="tab.advancedFilter" /></span>
            </template>
          </template>
        </nav>
      </div>
    </div>
  </div>
</template>